
import { Text, Link, Button, View, } from '@smarterservices/smarter-ui';
import React, { useEffect, useState } from 'react';
import { useAppStore } from '../../../stores/app';
import RemoteCameraList from './RemoteCameraList';
import SecondCameraVideo from './SecondCameraVideo';
import { usePaneViewer } from '../../PaneViewer';
import Loader from '../../Common/Loader';

/**
 * CameraConnected Component
 * 
 * Displays the interface when a second camera device is successfully connected to the proctoring session.
 * Provides camera selection controls, live video preview, and navigation to continue the session.
 * Handles loading states during camera changes and integrates with the pane viewer system.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.stepData - Step configuration data containing display texts
 * @param {string} props.stepData.continueButtonText - Text for the continue button
 * @param {Function} props.setRightPaneContent - Function to change the right pane content (unused in current implementation)
 * @returns {React.ReactElement} Camera connected interface with controls and video preview
 */
const CameraConnected = ({ stepData, setRightPaneContent }) => {
    // Get required data and functions from stores and hooks
    const mediaAgent = useAppStore((state) => state.mediaAgent);
    const { onNext, setIsTaskComplete } = usePaneViewer();
    const secondCameraDeviceData = useAppStore((state) => state.secondCameraDeviceData);

    // Local state to track camera change loading status
    const [isCameraChangeLoading, setIsCameraChangeLoading] = useState(false);

    /**
     * Handles the continue button click to proceed to the next step
     * Marks the current task as complete and navigates to the next step
     */
    const handleNext = () => {
        setIsTaskComplete(true);
        // Camera connection was successful, move to next step
        onNext();
    };

    return (
        <>
            {/* Main camera interface - hidden during camera changes */}
            <View as="div" style={{ display: isCameraChangeLoading ? 'none' : 'block' }}>
                {/* Camera selection dropdown with refresh functionality */}
                <RemoteCameraList
                    mediaAgent={mediaAgent}
                    onCameraChangeStart={() => setIsCameraChangeLoading(true)}
                    onCameraChangeEnd={(data) => {
                        if (data.successful) {
                            setIsCameraChangeLoading(false);
                        }
                    }}
                />

                {/* Live video preview from the second camera */}
                <SecondCameraVideo />
            </View>

            {/* Conditional rendering: loading state or continue button */}
            {isCameraChangeLoading ? (
                <Loader text="Changing camera..." />
            ) : (
                <Button
                    color="primary"
                    size="medium"
                    onClick={handleNext}
                    margin="small 0"
                >
                    {stepData?.continueButtonText}
                </Button>
            )}
        </>
    );
};

export default CameraConnected;
